const DEFAULT_TEMPO = 80;
const DEFAULT_BEATS_PER_MEASURE = 4;

const toNumber = (value) => {
  const num = Number(value);
  return Number.isFinite(num) ? num : null;
};

export function parseChordProTiming(chordPro = "") {
  const tempoMatch = chordPro.match(/\{tempo:\s*([0-9]+)\s*\}/i);
  const timeMatch = chordPro.match(/\{time:\s*([0-9]+)\s*\/\s*([0-9]+)\s*\}/i);

  const tempo = tempoMatch ? parseInt(tempoMatch[1], 10) : DEFAULT_TEMPO;
  const beatsPerMeasure = timeMatch ? parseInt(timeMatch[1], 10) : DEFAULT_BEATS_PER_MEASURE;

  return {
    bpm: Number.isFinite(tempo) && tempo > 0 ? tempo : DEFAULT_TEMPO,
    beatsPerMeasure: Number.isFinite(beatsPerMeasure) && beatsPerMeasure > 0 ? beatsPerMeasure : DEFAULT_BEATS_PER_MEASURE,
  };
}

export function deriveTimingMeta({ chordPro, guideTimeline }) {
  const guideBpm = toNumber(guideTimeline?.bpm ?? guideTimeline?.tempo);
  const guideBeatsPerMeasure = toNumber(
    guideTimeline?.beats_per_measure ?? guideTimeline?.beatsPerMeasure ?? guideTimeline?.time_signature?.[0],
  );
  if (guideTimeline && guideTimeline.ok !== false && guideBpm && guideBeatsPerMeasure) {
    return {
      bpm: guideBpm,
      beatsPerMeasure: guideBeatsPerMeasure,
    };
  }
  return parseChordProTiming(chordPro || "");
}

export function buildMeasureBoundaryBeats(sections, beatsPerMeasure) {
  const beats = Number.isFinite(beatsPerMeasure) && beatsPerMeasure > 0 ? beatsPerMeasure : DEFAULT_BEATS_PER_MEASURE;
  const boundaries = new Set();
  const ordered = (sections || [])
    .slice()
    .sort((a, b) => Number(a?.start_beat ?? a?.startBeat ?? 0) - Number(b?.start_beat ?? b?.startBeat ?? 0));
  const firstStart = toNumber(ordered[0]?.start_beat ?? ordered[0]?.startBeat ?? 0);
  boundaries.add(Number.isFinite(firstStart) ? firstStart : 0);

  ordered.forEach((section) => {
    if (section?.suppress_downbeat) return;
    const startBeat = toNumber(section?.start_beat ?? section?.startBeat ?? 0);
    if (!Number.isFinite(startBeat)) return;
    boundaries.add(startBeat);
    const explicitBeats = toNumber(section?.beats);
    const measures = toNumber(section?.measures);
    if (Number.isFinite(explicitBeats) && explicitBeats > 0) {
      const fullMeasures = Math.floor(explicitBeats / beats);
      for (let i = 1; i <= fullMeasures; i += 1) {
        boundaries.add(startBeat + i * beats);
      }
      return;
    }
    if (Number.isFinite(measures) && measures > 0) {
      for (let i = 1; i <= measures; i += 1) {
        boundaries.add(startBeat + i * beats);
      }
    }
  });

  const boundaryArray = Array.from(boundaries).filter(Number.isFinite).sort((a, b) => a - b);
  return {
    boundaryArray,
    boundarySet: new Set(boundaryArray),
  };
}

export function getNextBoundaryBeat(boundaryArray, currentBeat) {
  if (!boundaryArray?.length || !Number.isFinite(currentBeat)) return null;
  const epsilon = 0.0001;
  const target = currentBeat - epsilon;
  for (let i = 0; i < boundaryArray.length; i += 1) {
    if (boundaryArray[i] >= target) return boundaryArray[i];
  }
  return null;
}

export function getSecondsPerBeat(bpm) {
  if (!Number.isFinite(bpm) || bpm <= 0) return null;
  return 60 / bpm;
}

export function getBeatsPerMeasure(beatsPerMeasure) {
  return Number.isFinite(beatsPerMeasure) && beatsPerMeasure > 0 ? beatsPerMeasure : DEFAULT_BEATS_PER_MEASURE;
}
